import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Tooltip } from '@eposnow/ui-core';
import { UIContext } from '../../context/UIContext';
import { DataGridContext } from '../../context/DataGridContext';

const MobileRowActions = ({
  objectId,
  showDropdownMenu = false,
  customButtons = [],
  editAction,
}: {
  objectId: number;
  showDropdownMenu?: boolean;
  customButtons?: { id: string; label: string; action: () => void; icon?: React.ReactNode }[];
  editAction?: () => void;
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { colorMode } = useContext(UIContext);
  const { startEditObject, deleteObject, isLoadingDelete, objectIdBeingDeleted } =
    useContext(DataGridContext);
  const isDeleting = objectIdBeingDeleted === objectId.toString() && isLoadingDelete;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  let deleteIcon;

  if (isDeleting) {
    deleteIcon = <CircularProgress color="inherit" size="24px" />;
  } else {
    deleteIcon = <DeleteOutlineOutlinedIcon fontSize="medium" />;
  }

  return showDropdownMenu ? (
    <>
      <IconButton
        onClick={handleMenuOpen}
        sx={{
          color: 'text.secondary',
          ':hover': {
            color: 'text.primary',
            backgroundColor: colorMode === 'light' ? 'grey.4p' : 'elevation.4dp',
          },
          mr: 2,
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (editAction) editAction();
            else if (startEditObject) startEditObject(objectId.toString());
          }}
        >
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('components.gridActionsMenu.edit')} />
        </MenuItem>
        {deleteObject && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              deleteObject(objectId.toString());
            }}
          >
            <ListItemIcon>{deleteIcon}</ListItemIcon>
            <ListItemText primary={t('components.gridActionsMenu.delete')} />
          </MenuItem>
        )}
        {customButtons?.map((button) => (
          <MenuItem
            key={button.id}
            onClick={() => {
              handleMenuClose();
              button.action();
            }}
          >
            <ListItemIcon>{button.icon}</ListItemIcon>
            <ListItemText primary={button.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  ) : (
    <Box display="flex">
      <Tooltip title={t('components.gridActionsMenu.edit')} theme={theme}>
        <IconButton
          sx={{
            color: 'text.secondary',
            ':hover': {
              color: 'text.primary',
              backgroundColor: colorMode === 'light' ? 'grey.4p' : 'elevation.4dp',
            },
          }}
          size="small"
          aria-label={t('components.gridActionsMenu.edit')}
          onClick={() => startEditObject(objectId.toString())}
        >
          <EditOutlinedIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('components.gridActionsMenu.delete')} theme={theme}>
        <IconButton
          sx={{
            color: 'text.secondary',
            ':hover': {
              color: 'text.primary',
              backgroundColor: colorMode === 'light' ? 'grey.4p' : 'elevation.4dp',
            },
          }}
          size="small"
          aria-label={t('components.gridActionsMenu.delete')}
          onClick={() => deleteObject(objectId.toString())}
        >
          {deleteIcon}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default MobileRowActions;
